/* Header behaviour — extracted verbatim from sections/header.liquid (AAS-5454).

   Was an 880-line inline <script> in the header section, so ~45 KB of identical
   JavaScript shipped uncached on EVERY page of the site. As an asset the browser
   fetches it once and reuses it, and the HTML shrinks by the same amount
   everywhere, not just on the collection pages the ticket names.

   Two theme-editor settings used to be interpolated straight into the source:

       blur_height_desktop | default: 80
       blur_height_mobile  | default: 60

   They now arrive as data attributes on <sticky-header> and are read at the
   point of use, so this file contains no Liquid and can be cached.

   Everything else is byte-for-byte the original. This is a move, not a rewrite.
*/
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
      this.headerInner = this.header.querySelector('.header');
      this.uspBar = this.header.querySelector('.header__usp');
      this.headerBounds = {};

      this.setHeaderHeight();
      this.setUspHeight();
      this.boundUspMarquee = this.setupUspMarquee.bind(this);
      this.boundUspMarquee();
      // Late font swaps and viewport changes move the overflow boundary —
      // re-measure (the 400px viewport also shrinks on narrow screens).
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.boundUspMarquee);
      window.addEventListener('resize', this.boundUspMarquee);

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.boundCreateObserver = this.createObserver.bind(this);
      this.createObserver();
      window.addEventListener('resize', this.boundCreateObserver);
    }

    setHeaderHeight() {
      if (this.headerResizeObserver) return;
      // Freeze --header-height for the whole mobile nav-drawer cycle. Opening
      // adds .drawer-open, which display:none's the logo/heading below 1024px
      // (component-nav-drawer.css) and shrinks .header; writing that transient
      // height into --header-height would nudge every element spaced off it —
      // notably the PDP hero's `margin-top: calc(-1 * var(--header-height))`
      // + matching padding-top. Page layout only ever needs the CLOSED-state
      // height, so skip while the drawer is open or animating closed. AAS-1169
      const writeHeaderHeight = () => {
        var wrapper = this.headerInner.closest('.header-wrapper');
        if (wrapper && (wrapper.classList.contains('drawer-open') || wrapper.classList.contains('drawer-closing')))
          return;
        document.documentElement.style.setProperty('--header-height', `${this.headerInner.offsetHeight}px`);
      };
      this.headerResizeObserver = new ResizeObserver(writeHeaderHeight);
      this.headerResizeObserver.observe(this.headerInner);
      // The freeze can go stale: if .header resizes WHILE the drawer is open —
      // e.g. crossing the 1024px breakpoint auto-closes the drawer (theme.js)
      // but the 64→92px bar resize fired the observer inside the frozen window —
      // nothing re-fires the observer after the cycle ends. The drawer's
      // close-finish dispatches drawer-height-changed AFTER clearing
      // drawer-open/-closing, so re-run the write then; every earlier dispatch
      // (open, height tween, close start) is a no-op via the guard above. AAS-1169
      const drawerEl = this.headerInner.closest('.header-wrapper')?.querySelector('nav-drawer');
      if (drawerEl) drawerEl.addEventListener('drawer-height-changed', writeHeaderHeight);
    }
    // Feeds the sticky-offset math: the header sticks at top: -usp-height so the
    // USP bar scrolls out of view above it. Measured (not a fixed 30px) so a
    // wrapped bar on mobile still offsets correctly. 0 when there is no bar.
    // AAS-948: 400px text viewport. Overflowing text is duplicated into the
    // track for a seamless loop; the scroll distance (one copy + gap) and a
    // constant-speed duration are published as CSS vars, and .is-overflowing
    // switches on the edge fades + marquee animation (section-header.css).
    // Idempotent: every run resets to a single copy before measuring, so it
    // is safe on fonts.ready and resize.
    setupUspMarquee() {
      const bar = this.uspBar;
      if (!bar) return;
      const viewport = bar.querySelector('.header__usp-viewport');
      const track = bar.querySelector('.header__usp-track');
      const text = bar.querySelector('.header__usp-text');
      if (!viewport || !track || !text) return;

      track.querySelectorAll('.header__usp-text[aria-hidden]').forEach((n) => n.remove());
      bar.classList.remove('is-overflowing');
      track.style.removeProperty('--usp-scroll-distance');
      track.style.removeProperty('--usp-marquee-duration');

      const textWidth = text.scrollWidth;
      if (textWidth <= viewport.clientWidth) return;

      const copy = text.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      // The seamless-loop copy is decorative: any link the merchant put in the
      // richtext would otherwise be a second, aria-hidden-but-focusable tab
      // stop pointing at the same URL.
      copy.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
      track.appendChild(copy);

      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const distance = textWidth + gap;
      // "Slowly": constant 40px/s whatever the length — duration scales with
      // the text instead of the loop spinning faster for long messages.
      const SPEED_PX_PER_S = 40;
      track.style.setProperty('--usp-scroll-distance', distance + 'px');
      track.style.setProperty('--usp-marquee-duration', distance / SPEED_PX_PER_S + 's');
      bar.classList.add('is-overflowing');
    }

    setUspHeight() {
      if (!this.uspBar) {
        this.header.style.setProperty('--usp-height', '0px');
        document.documentElement.style.setProperty('--usp-height', '0px');
        return;
      }
      const resizeObserver = new ResizeObserver(() => {
        // Published on :root as well (like --header-blur-h) — content outside
        // the header section that anchors against the header must account for
        // the bar too; see the PDP floating breadcrumb in breadcrumb.css.
        this.header.style.setProperty('--usp-height', `${this.uspBar.offsetHeight}px`);
        document.documentElement.style.setProperty('--usp-height', `${this.uspBar.offsetHeight}px`);
      });
      resizeObserver.observe(this.uspBar);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      window.removeEventListener('resize', this.boundCreateObserver);
      window.removeEventListener('resize', this.boundUspMarquee);
    }

    createObserver() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.headerBounds = entries[0].intersectionRect;
        },
        { threshold: [0] }
      );

      this.intersectionObserver.observe(this.header);
    }

    onScroll() {
      // Coalesce the scroll-event burst into one rAF callback per frame.
      if (this.scrollRafPending) return;
      this.scrollRafPending = true;
      requestAnimationFrame(() => {
        this.scrollRafPending = false;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          this.headerInner.classList.add('is-sticky');
          // AAS-948: scrolling down — the USP bar slides away (sticky top
          // returns to -usp-height, see .usp-revealed in section-header.css).
          this.header.classList.remove('usp-revealed');
          this.hide();
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          this.headerInner.classList.add('is-sticky');
          if (!this.preventReveal) {
            // AAS-948: scrolling up — slide the USP bar back in.
            this.header.classList.add('usp-revealed');
            this.reveal();
          } else {
            window.clearTimeout(this.isScrolling);
            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);
            this.header.classList.remove('usp-revealed');
            this.hide();
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          this.headerInner.classList.remove('is-sticky');
          // AAS-948: back at the top the section un-sticks into normal flow —
          // the bar is naturally visible; drop the reveal so the resting
          // sticky offset invariant (-usp-height) is restored.
          this.header.classList.remove('usp-revealed');
          this.reset();
        }

        this.currentScrollTop = scrollTop;
      });
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden');
      this.header.classList.add('shopify-section-header-sticky', 'animate');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }
  }

  customElements.define('sticky-header', StickyHeader);

  (function () {
    const announcementHeader = document.querySelector('.shopify-section-group-header-group');
    const announcementScrollingHeight = announcementHeader?.querySelector('.section-scrolling-text')?.clientHeight;

    if (announcementScrollingHeight) {
      document.documentElement.style.setProperty('--announcement-scrolling-height', `${announcementScrollingHeight}px`);
    }

    var burgerBtn = document.querySelector('.header__burger');
    var navDrawer = document.querySelector('nav-drawer');
    if (burgerBtn && navDrawer) {
      burgerBtn.addEventListener('click', function () {
        navDrawer.toggle(burgerBtn);
      });
    }

    const bgLayer = document.querySelector('.header__bg-layer');
    if (bgLayer) {
      const headerWrapper = bgLayer.parentElement;

      var header = headerWrapper.querySelector('.header');
      var gradientShadow = headerWrapper.querySelector('.header__gradient-shadow');
      var bgLine = headerWrapper.querySelector('.header__bg-line');
      var flyoutSheet = headerWrapper.querySelector('.header__flyout-sheet');
      var closedSheet = headerWrapper.querySelector('.header__closed-sheet');

      // AAS-5270: --drawer-h goes straight to the elements that read it (their
      // .drawer-closing rules in section-header.css), never to .header-wrapper.
      // Skipping an unchanged value also stops the loop's idle tail frames from
      // invalidating anything at all.
      var lastDrawerH = null;
      function setDrawerH(px) {
        if (px === lastDrawerH) return;
        lastDrawerH = px;
        var value = px + 'px';
        bgLayer.style.setProperty('--drawer-h', value);
        if (bgLine) bgLine.style.setProperty('--drawer-h', value);
        if (gradientShadow) gradientShadow.style.setProperty('--drawer-h', value);
      }
      var logoEl = headerWrapper.querySelector('.header__heading-link');
      // AAS-5243: the notch's horizontal anchor box — the notch shapes trace
      // wherever the grid puts this element, not an assumed viewport center.
      var logoNotchEl = headerWrapper.querySelector('.header__logo-notch');
      var flyoutCloseTimer = null;
      // Close grace: syncBgLayer arms this instead of tearing the open state
      // down the instant no mega matches — a pointer dipping past the flyout's
      // bottom edge for a frame (easy near the bottom actions / notch logo)
      // must not remove and re-add .flyout-open, which replays every keyframe.
      var flyoutCloseIntent = null;
      // When the last close actually started — the rescue path in syncBgLayer
      // compares it against the slide-up's 0.15s keyframe delay to pick the
      // seamless vs replay recovery. AAS-1169
      var flyoutCloseStartedAt = 0;

      // The notch logo's resting offset from the wrapper top — measured only
      // while closed, so its open-state transform isn't baked into the reading.
      // Used to compute how far it must travel into the flyout's bottom notch.
      var logoBaseTop = 0;
      function measureLogoBase() {
        if (!logoEl || headerWrapper.classList.contains('flyout-open')) return;
        logoBaseTop = logoEl.getBoundingClientRect().top - headerWrapper.getBoundingClientRect().top;
      }
      measureLogoBase();

      // Input modality: while tabbing, mark the header keyboard-driven so focus —
      // not a resting mouse pointer — decides which mega is open (see the
      // .using-keyboard rules in section-header.css). Any pointer activity hands
      // control back to hover.
      function setKeyboardMode(on) {
        if (headerWrapper.classList.contains('using-keyboard') === on) return;
        headerWrapper.classList.toggle('using-keyboard', on);
        wakeSync();
      }
      window.addEventListener('keydown', function (e) {
        if (e.key === 'Tab') setKeyboardMode(true);
      });
      // Last pointer modality that touched the header — 'mouse' | 'touch' | 'pen'.
      // The L1 two-tap guard (AAS-4162) keys off this, NOT a media query: a hybrid
      // touch-laptop reports (hover: hover) yet still taps, and a mouse user on that
      // same machine must keep instant hover-nav. Reading the live pointerType is the
      // only signal that separates the two per interaction.
      var lastPointerType = 'mouse';
      window.addEventListener('pointerdown', function (e) {
        if (e.pointerType) lastPointerType = e.pointerType;
        setKeyboardMode(false);
      });
      window.addEventListener('pointermove', function () {
        setKeyboardMode(false);
      });

      // One MQL object for the desktop probe — syncBgLayer reads .matches every
      // frame while the loop runs, so don't re-parse the query per call.
      var desktopMQ = window.matchMedia('(min-width: 990px)');
      // Touch-primary device (tablet, DevTools emulation). Read by the hover
      // intent below and the touch two-tap (AAS-4162) — see isTouchNav.
      var coarseMQ = window.matchMedia('(hover: none), (pointer: coarse)');

      // Bottom-notch clip-path polygon for a panel of the given pixel height.
      function notchClip(height, isDesktop) {
        var outerHalf = isDesktop ? 60 : 50;
        var innerHalf = isDesktop ? 33 : 27;
        var notchH = isDesktop ? 40 : 35;
        // AAS-5243: x-anchor is var(--notch-cx, 50%) — the measured logo-notch
        // center published by syncBgLayer. The header grid's 1fr side columns
        // stop being equal once the menu's min-content overflows its share
        // (long labels, e.g. the German market, at narrow widths), which
        // pushes the auto logo column off the 50% the shapes used to assume.
        return (
          'polygon(0 0,100% 0,100% ' +
          height +
          'px,' +
          'calc(var(--notch-cx,50%) + ' +
          outerHalf +
          'px) ' +
          height +
          'px,' +
          'calc(var(--notch-cx,50%) + ' +
          innerHalf +
          'px) ' +
          (height - notchH) +
          'px,' +
          'calc(var(--notch-cx,50%) - ' +
          innerHalf +
          'px) ' +
          (height - notchH) +
          'px,' +
          'calc(var(--notch-cx,50%) - ' +
          outerHalf +
          'px) ' +
          height +
          'px,' +
          '0 ' +
          height +
          'px)'
        );
      }

      // AAS-948: the closed-sheet's clip — notchClip flipped vertically. The
      // notch PROTRUDES below the flat strip (stripH tall; 0 without the USP
      // bar) instead of cutting into it, so the parked closed surface shows a
      // downward-facing notch exactly where the logo sits. Same outer/inner
      // geometry as notchClip, so the closed and open notches are congruent.
      function notchClipFlipped(stripH, isDesktop) {
        var outerHalf = isDesktop ? 60 : 50;
        var innerHalf = isDesktop ? 33 : 27;
        var notchH = isDesktop ? 40 : 35;
        var tip = stripH + notchH;
        return (
          'polygon(0 0,100% 0,100% ' + stripH + 'px,' +
          'calc(var(--notch-cx,50%) + ' + outerHalf + 'px) ' + stripH + 'px,' +
          'calc(var(--notch-cx,50%) + ' + innerHalf + 'px) ' + tip + 'px,' +
          'calc(var(--notch-cx,50%) - ' + innerHalf + 'px) ' + tip + 'px,' +
          'calc(var(--notch-cx,50%) - ' + outerHalf + 'px) ' + stripH + 'px,' +
          '0 ' + stripH + 'px)'
        );
      }

      // The real close, fired once the 150ms grace elapses without a mega
      // re-matching: play the slide-up, then drop the state after the close
      // animation. --flyout-h stays set so the closing transform is correct.
      // (openMegaItem / the hold helpers are declared below; they're var/function
      // scoped and this only ever runs via the timer.)
      function startFlyoutClose() {
        flyoutCloseIntent = null;
        flyoutCloseStartedAt = performance.now();
        headerWrapper.classList.remove('flyout-open', 'flyout-resizing', 'flyout-switching');
        headerWrapper.classList.add('flyout-closing');
        if (openMegaItem) setItemExpanded(openMegaItem, false);
        if (flyoutCloseTimer) clearTimeout(flyoutCloseTimer);
        flyoutCloseTimer = setTimeout(function () {
          headerWrapper.classList.remove('flyout-closing', 'flyout-rescue');
          flyoutCloseTimer = null;
          // Only now does the open item forget its panel was showing — the
          // column cascade is keyed to .is-mega-open (component-mega-menu-
          // atomic.css), so it re-runs on the next genuine open, never on a
          // rescued dip. AAS-1169
          if (openMegaItem) openMegaItem.classList.remove('is-mega-open');
          openMegaItem = null;
          releaseFlyoutHold(); // drop any stray .is-held once fully closed
          measureLogoBase();
          // 600 = slide-up delay 0.15s + duration 0.4s (+ buffer); keep in sync
          // with the .flyout-closing animation in section-header.css. AAS-1169
        }, 600);
        wakeSync();
      }

      function syncBgLayer() {
        var wrapperRect = headerWrapper.getBoundingClientRect();
        var wrapperTop = wrapperRect.top;
        var headerBottom = header.getBoundingClientRect().bottom;
        var isDesktop = desktopMQ.matches;

        // AAS-5243: publish the notch's real horizontal center. Every notch
        // polygon (twin + contour, flyout clip, bg-line, gradient shadow, mega
        // cutout, drawer navigation) anchors at var(--notch-cx, 50%); the px
        // value here overrides the 50% fallback with where the grid actually
        // put the logo. Skipped while the logo is hidden (mobile drawer open —
        // width reads 0), so the last closed-state anchor holds through the
        // drawer cycle. Rounded: sub-pixel churn would keep the idle loop
        // alive via the signature below.
        var notchCx = 0;
        if (logoNotchEl) {
          var logoNotchRect = logoNotchEl.getBoundingClientRect();
          if (logoNotchRect.width > 0) {
            notchCx = Math.round(logoNotchRect.left + logoNotchRect.width / 2 - wrapperRect.left);
            headerWrapper.style.setProperty('--notch-cx', notchCx + 'px');
          }
        }
        // Closed-nav frost band height — theme-editor testing knobs (AAS-4124);
        // chosen defaults 80/60 (was hardcoded 92/64), Liquid fallbacks cover
        // an unset/missing setting.
        // Theme-editor values, previously interpolated by Liquid at this exact
        // spot. They now ride on <sticky-header> as data attributes so this file
        // stays static and cacheable; the 80/60 fallbacks match the Liquid
        // `| default:` filters they replace, and also cover the element being
        // absent entirely.
        var stickyEl = document.querySelector('sticky-header');
        var blurDesktop = parseInt(stickyEl && stickyEl.dataset.blurHeightDesktop, 10);
        var blurMobile = parseInt(stickyEl && stickyEl.dataset.blurHeightMobile, 10);
        if (isNaN(blurDesktop)) blurDesktop = 80;
        if (isNaN(blurMobile)) blurMobile = 60;
        var closedH = isDesktop ? blurDesktop : blurMobile;
        // AAS-948: the closed band is anchored at the wrapper top (CSS top: 0),
        // independent of the USP bar — with the bar active the band underlays
        // it, growing by the bar height so its bottom edge below the header
        // row keeps the configured depth. The published value describes the
        // same region measured from the viewport top for content that must
        // clear it.
        var uspEl = headerWrapper.querySelector('.header__usp');
        var uspH = uspEl ? uspEl.offsetHeight : 0;
        // Publish the closed frost band so content underneath can clear it in
        // CSS. The band is taller than the header bar itself and carries the
        // progressive blur, so anything sitting in it renders blurred — see the
        // PDP breadcrumb in breadcrumb.css. It is a theme-editor setting
        // (blur_height_desktop/mobile), hence a variable rather than a constant
        // anyone could hardcode and have drift. AAS-4639
        document.documentElement.style.setProperty('--header-blur-h', (closedH + uspH) + 'px');
        var notchH = isDesktop ? 40 : 35;

        // AAS-948: park the closed-sheet — flat strip as tall as the USP bar
        // (0 without it) with the flipped notch protruding below. When the
        // sticky section offsets by --usp-height on scroll, the whole shape
        // rides up with the wrapper and only the notch stays visible.
        if (closedSheet) {
          closedSheet.style.height = (uspH + notchH) + 'px';
          closedSheet.style.clipPath = notchClipFlipped(uspH, isDesktop);
        }

        // ---- Desktop mega: tallest open dropdown ----
        // :hover (mouse) and :focus-within (keyboard tab) both open a dropdown.
        // .is-held keeps the open item's dropdown counted while the pointer or
        // focus is on a shared control OUTSIDE the item's subtree — the bottom
        // actions cluster or the traveling notch logo — where :hover has already
        // dropped (see the hold-zone wiring below).
        // While keyboard-driven, focus is authoritative — ignore hovered megas so
        // a resting pointer can't stretch the backdrop to a second open panel.
        // AAS-4073: and the reverse. Under pointer control :focus-within is NOT
        // counted, because the CSS now hides that panel (section-header.css) once
        // the mouse takes over — a stale keyboard focus left in the selector kept
        // measuring an invisible panel and stretched the backdrop to its height.
        // Each branch must list exactly the triggers its modality lets paint.
        // .is-dismissed (AAS-5493): a click on the open panel's blank background
        // closed the flyout — but :hover still matches while the pointer rests
        // there, so the dismissed item must not count as open or the sheet
        // would re-measure it and snap back. Cleared on pointerleave/reopen.
        // .is-hover-open, not :hover (AAS-5653) — the pointer trigger is stamped
        // after a dwell so a transit across the header opens nothing; the sheet
        // must measure exactly what the CSS paints, so it reads the same class.
        var megaSel = headerWrapper.classList.contains('using-keyboard')
          ? '.list-menu__has-mega:focus-within .atomic-mega, .list-menu__has-mega.is-held .atomic-mega'
          : '.list-menu__has-mega.is-hover-open:not(.is-dismissed) .atomic-mega, .list-menu__has-mega.is-held:not(.is-dismissed) .atomic-mega';
        var megaBottom = headerBottom;
        header.querySelectorAll(megaSel).forEach(function (m) {
          var b = m.getBoundingClientRect().bottom;
          if (b > megaBottom) megaBottom = b;
        });

        // ---- Mobile nav-drawer (still uses .header__bg-layer as its backdrop) ----
        var drawerEl = headerWrapper.querySelector('nav-drawer.is-open');
        var drawerBottom = headerBottom;
        // Raw rect bottom, unclamped — the signature needs it. While the drawer
        // slides in (translateY(-100%) → 0) its rect bottom is still ABOVE
        // headerBottom, so the clamped drawerBottom reads as a constant
        // headerBottom frame after frame; with every other input static the
        // loop saw 2 stable frames and idled out mid-flight — and nothing woke
        // it again (a transform doesn't resize the drawer's box, so the panel
        // ResizeObserver never fires). Result: frost stuck at closedH under a
        // fully open drawer. The raw bottom changes every frame of the slide,
        // so the loop provably stays alive until the drawer lands. AAS-1169
        var drawerRectBottom = headerBottom;
        if (drawerEl) {
          var db = drawerEl.getBoundingClientRect().bottom;
          drawerRectBottom = db;
          if (db > drawerBottom) drawerBottom = db;
        }

        var drawerOpen = drawerBottom > headerBottom + 2;
        var megaOpen = megaBottom > headerBottom + 2;

        // Mobile drawer: unchanged — bg-layer grows to the drawer and frosts via
        // .drawer-open (CSS). Closed state: fixed 92/64px header background.
        if (drawerOpen) {
          var dh = drawerBottom - wrapperTop;
          bgLayer.style.height = dh + 'px';
          bgLayer.style.clipPath = notchClip(dh, isDesktop);
          if (bgLine) bgLine.style.height = dh + 'px';
          if (gradientShadow) gradientShadow.style.top = dh - (notchH + 1) + 'px';
          // Publish the drawer height so the frost, contour and gradient can all
          // slide up by it on .drawer-closing (one transform slide) instead of
          // hanging in place and fading. Persists after close (never cleared) so
          // the closing transform target is correct. Mobile only. AAS-1169
          //
          // AAS-5270: written on the three consumers, NOT on .header-wrapper.
          // This runs every frame of the slide-in, and a custom property on the
          // wrapper invalidates style for everything under it — the whole header
          // subtree, plus a re-run of the wrapper's :has() rules — once per
          // frame, while the item cascade is trying to animate. These three
          // elements are the only readers (see .drawer-closing in
          // section-header.css), so scoping the write there keeps the value
          // identical and drops the invalidation to their own small subtrees.
          setDrawerH(dh);
        } else if (headerWrapper.classList.contains('drawer-closing')) {
          // Closing: keep the frost's OPEN height + notch clip so the whole panel
          // (notch intact) slides up via transform (CSS .drawer-closing), riding
          // up like the drawer itself. Resetting to closedH / clearing the clip
          // here would collapse the notch and fill the cutout with frost as it
          // recedes (the white-cutout glitch). Restored once .drawer-closing
          // clears and this falls through to the plain-closed branch. AAS-1169
        } else {
          // closedH is the depth below the header row; the band starts at the
          // wrapper top, so with a USP bar it underlays the bar too.
          bgLayer.style.height = (closedH + uspH) + 'px';
          bgLayer.style.clipPath = '';
        }

        // Desktop mega flyout: drive the dedicated sliding sheet (AAS-1169). The
        // sheet, contour and shadow all slide by the shared --flyout-h; the notch
        // logo travels into the bottom notch via --logo-translate.
        if (megaOpen) {
          // Already open → the pointer moved between L1 items whose dropdowns
          // differ in height. Flag .flyout-resizing so the sheet/contour/shadow
          // TRANSITION to the new height (like the logo) instead of jumping; a
          // fresh open keeps the class off so the slide keyframe runs clean.
          var wasFlyoutOpen = headerWrapper.classList.contains('flyout-open');
          var mh = megaBottom - wrapperTop;
          headerWrapper.style.setProperty('--flyout-h', mh + 'px');
          headerWrapper.style.setProperty('--flyout-clip', notchClip(mh, isDesktop));
          headerWrapper.style.setProperty('--logo-translate', mh - notchH - logoBaseTop + 'px');
          if (flyoutSheet) flyoutSheet.style.height = mh + 'px';
          if (!drawerOpen && bgLine) bgLine.style.height = mh + 'px';
          if (!drawerOpen && gradientShadow) gradientShadow.style.top = mh - (notchH + 1) + 'px';

          if (flyoutCloseIntent) {
            clearTimeout(flyoutCloseIntent);
            flyoutCloseIntent = null;
          }
          if (flyoutCloseTimer) {
            clearTimeout(flyoutCloseTimer);
            flyoutCloseTimer = null;
          }
          // Rescuing a close already in progress. Inside the slide-up's 0.15s
          // keyframe delay the sheet hasn't visibly moved, so flag .flyout-rescue
          // (section-header.css): re-adding .flyout-open must NOT replay the
          // slide-down — the bare class flap would jump the sheet to hidden and
          // re-run it. Past the delay the recede is visible and the sheet
          // re-slides from the top anyway, so re-run the column cascade with it
          // (.is-mega-open flap across a forced reflow) and drop any earlier
          // rescue + .flyout-switching so the entrance runs as a fresh open.
          // Threshold keyed to the .flyout-closing delay in section-header.css.
          // AAS-1169
          if (!wasFlyoutOpen && headerWrapper.classList.contains('flyout-closing')) {
            if (performance.now() - flyoutCloseStartedAt < 150) {
              headerWrapper.classList.add('flyout-rescue');
            } else {
              headerWrapper.classList.remove('flyout-rescue', 'flyout-switching');
              if (openMegaItem) {
                openMegaItem.classList.remove('is-mega-open');
                void openMegaItem.offsetWidth;
                openMegaItem.classList.add('is-mega-open');
              }
            }
            // The close start reset aria-expanded; a rescue reached through the
            // hold zone (not onItemOpen) needs it re-asserted. AAS-1169
            if (openMegaItem) setItemExpanded(openMegaItem, true);
          }
          headerWrapper.classList.remove('flyout-closing');
          headerWrapper.classList.add('flyout-open');
          headerWrapper.classList.toggle('flyout-resizing', wasFlyoutOpen);
        } else if (headerWrapper.classList.contains('flyout-open')) {
          // Un-hover: don't tear down yet — arm the 150ms close grace. A pointer
          // dipping past the flyout's bottom edge for a frame or two re-cancels
          // it (open branch above) with every keyframe state intact; a real
          // leave only defers the visible close by the grace, and the panel's
          // hover-keyed opacity fade starts immediately regardless. AAS-1169
          if (!flyoutCloseIntent) {
            flyoutCloseIntent = setTimeout(startFlyoutClose, 150);
          }
        }

        // Signature of every geometry input the writes above derive from. The
        // loop uses it to detect when nothing more is moving and idle out.
        return (
          (isDesktop ? 'd' : 'm') +
          ',' +
          Math.round(wrapperTop) +
          ',' +
          Math.round(headerBottom) +
          ',' +
          Math.round(megaBottom) +
          ',' +
          Math.round(drawerRectBottom) +
          ',' +
          notchCx +
          ',' +
          (headerWrapper.classList.contains('using-keyboard') ? 'k' : '')
        );
      }

      // --- Backdrop / flyout geometry sync (event-driven, self-terminating) ---
      // The open/close SLIDE is pure CSS (keyframes off --flyout-h); JS only has
      // to publish the target geometry when it actually changes. So rather than a
      // rAF loop re-asserting constant values every frame for the whole hover,
      // the loop idles once syncBgLayer's signature holds for two frames — it
      // runs only while something is genuinely moving (open/close, the drawer
      // height animation, resize) and stops otherwise. Any interaction wakes it;
      // a ResizeObserver on the panels catches async reflow (web fonts, images)
      // that lands after the loop has idled.
      var rafId = null;
      var lastSig = null;
      var stableFrames = 0;

      function loop() {
        var sig = syncBgLayer();
        if (sig === lastSig) {
          if (++stableFrames >= 2) {
            rafId = null;
            return;
          }
        } else {
          stableFrames = 0;
          lastSig = sig;
        }
        rafId = requestAnimationFrame(loop);
      }

      function wakeSync() {
        lastSig = null;
        stableFrames = 0;
        if (rafId === null) rafId = requestAnimationFrame(loop);
      }

      // ---- Back-navigation restore (AAS-5567) ----
      // Back restores focus to the link that was clicked, which sits inside the
      // flyout, and `.list-menu__has-mega:focus-within .mega-menu__content`
      // (component-mega-menu-atomic.css:48) opens the panel on focus alone.
      // The sheet behind it is measured off megaSel, which only consults
      // :focus-within while .using-keyboard is set — after a mouse-driven Back
      // it is not. Hence the menu came back as text with no background, and any
      // click closed it: the click moved focus out.
      //
      // Dropped only when the restored focus landed inside a flyout; focus
      // restoration anywhere else on the page is left alone. Not gated on
      // event.persisted — a re-rendered back navigation lands in the same state.
      window.addEventListener('pageshow', function () {
        var focused = document.activeElement;
        if (focused && focused.closest && focused.closest('.list-menu__has-mega')) {
          focused.blur();
        }
        // A bfcache restore brings the DOM back exactly as it was left, so the
        // hover-intent stamp (AAS-5653) survives the navigation and would have
        // the flyout hanging open on arrival — with no pointer near it, and no
        // pointerleave coming to clear it. :hover could never do this; a class
        // can, so it has to be reset here. Anything still under the pointer
        // re-arms on the next dwell.
        var stale = headerWrapper.querySelectorAll('.list-menu__has-mega.is-hover-open');
        stale.forEach(function (item) {
          item.classList.remove('is-hover-open');
        });
        // The rAF loop idles out while nothing moves, and a restore wakes it for
        // nobody — without this the sheet keeps the height it was frozen at.
        if (stale.length) wakeSync();
      });

      if ('ResizeObserver' in window) {
        var panelRO = new ResizeObserver(wakeSync);
        header.querySelectorAll('.atomic-mega').forEach(function (m) {
          panelRO.observe(m);
        });
        if (navDrawer) panelRO.observe(navDrawer);
      }

      // The L1 item whose dropdown is currently open — the hold zone below
      // re-anchors the open state to it (via .is-held) when the pointer/focus
      // leaves the item for a shared control. Cleared once a close finishes.
      var openMegaItem = null;

      // Opening an item: flag .flyout-switching = was the flyout ALREADY open?
      // Set here (same event as :hover, before the style recalc) so the column
      // cascade reads it from its first frame — a switch fades only, a fresh open
      // slides in from above. Returning to the ALREADY-open item (e.g. back up
      // from the held bottom actions) is NOT a switch — flagging it would replay
      // the column fade. AAS-1169
      // Keep the L1 link's aria-expanded in step with the open flyout. The rework
      // drives open/close purely through CSS classes, so without this a screen
      // reader would hear the panel as "collapsed" while its links are live in the
      // tab sequence. Toggled on open/switch (below) and reset when the flyout
      // starts closing (syncBgLayer). AAS-1169
      function setItemExpanded(item, expanded) {
        var link = item && item.querySelector('.header__menu-item');
        if (link) link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }

      function onItemOpen(e) {
        var item = e.currentTarget;
        if (item !== openMegaItem) {
          if (openMegaItem) {
            setItemExpanded(openMegaItem, false);
            openMegaItem.classList.remove('is-mega-open');
          }
          headerWrapper.classList.toggle('flyout-switching', headerWrapper.classList.contains('flyout-open'));
        }
        var isNewPanel = item !== openMegaItem;
        openMegaItem = item;
        // .is-mega-open drives the column cascade (component-mega-menu-atomic
        // .css) instead of :hover/.is-held — hover matching is instantaneous, so
        // any one-frame gap (detour over the shared bottom actions / logo, dip
        // past the flyout's bottom edge) cancels a hover-keyed animation and
        // replays it on re-match. This class persists from open until an L1
        // switch or the close fully completes, so the cascade runs exactly once
        // per open/switch wherever the pointer wanders in between. AAS-1169
        item.classList.add('is-mega-open');
        // A fresh open always clears a stale click-dismiss (AAS-5493) — a
        // pointerleave normally clears it, but a touch tap has no leave.
        item.classList.remove('is-dismissed');
        // A viewport-capped panel keeps its scroll position in the DOM — a
        // fresh open or an L1 switch must start at the top (mid-scroll cascade
        // reads broken). Detours back to the already-open item must NOT reset,
        // hence the isNewPanel gate. AAS-1169
        if (isNewPanel) {
          var capScroller = item.querySelector('.atomic-mega__content-wrapper');
          if (capScroller) capScroller.scrollTop = 0;
        }
        setItemExpanded(item, true);
        releaseFlyoutHold();
        wakeSync();
      }
      // ---- Hover intent (AAS-5653) ----
      // The flyout used to open the instant the pointer crossed an L1 item, so a
      // mouse merely TRAVELLING over the header — up to the browser's Back button
      // and back down again — popped the menu open twice on the way. The standard
      // fix is to open only once the pointer has come to REST on the item: a
      // transit never dwells, an approach always does.
      //
      // The dwell is deliberately narrow — it applies to a cold open with a mouse
      // and nothing else:
      //   - switching L1 while a flyout is open (or still closing) is INSTANT.
      //     The user is demonstrably in the menu already; a delay there reads as
      //     lag, not as intent.
      //   - a hovering pen opens instantly — it has no transit worth filtering.
      //   - touch (and a pen on a touch tablet) never opens here at all: the
      //     two-tap click handler (AAS-4162) owns it. See onItemPointerEnter.
      //   - keyboard is untouched — focusin still calls onItemOpen directly, and
      //     the CSS keeps its own :focus-within trigger.
      //
      // The armed state is a class rather than :hover because :hover cannot be
      // deferred: the panel CSS (component-mega-menu-atomic.css), the header
      // chrome colour and the megaSel the backdrop sheet measures all read
      // .is-hover-open, so all three arm on the same frame.
      var HOVER_INTENT_MS = 120;
      var hoverIntentTimer = null;

      function cancelHoverIntent() {
        if (hoverIntentTimer === null) return;
        clearTimeout(hoverIntentTimer);
        hoverIntentTimer = null;
      }

      function armHoverOpen(item) {
        cancelHoverIntent();
        // A pointer that slid straight from one L1 to the next can leave the
        // previous stamp behind if its pointerleave was swallowed (drag out of
        // the window, a pointer capture): clear siblings so megaSel can never
        // measure two open panels and stretch the sheet to the taller one.
        headerWrapper.querySelectorAll('.list-menu__has-mega.is-hover-open').forEach(function (other) {
          if (other !== item) other.classList.remove('is-hover-open');
        });
        item.classList.add('is-hover-open');
        onItemOpen({ currentTarget: item });
      }

      function onItemPointerEnter(e) {
        // A finger (or a pen on a touch-primary tablet) enters the item only by
        // landing on it — that is a tap, and the two-tap click handler below
        // owns it (AAS-5727). Stamping the hover here broke iPad twice over:
        // touch fires pointerleave the moment the finger lifts, which wiped the
        // stamp again, and iOS Safari suppresses the click of any tap whose
        // touchstart reveals content (it treats that as a hover), so the
        // two-tap never ran to pin the flyout. Every tap flashed the panel open
        // and shut, and the L2/L3 links could never be reached.
        if (e.pointerType === 'touch' || (e.pointerType === 'pen' && coarseMQ.matches)) return;
        var item = e.currentTarget;
        var inMenuAlready =
          headerWrapper.classList.contains('flyout-open') || headerWrapper.classList.contains('flyout-closing');
        if (e.pointerType !== 'mouse' || inMenuAlready) {
          armHoverOpen(item);
          return;
        }
        cancelHoverIntent();
        hoverIntentTimer = setTimeout(function () {
          hoverIntentTimer = null;
          armHoverOpen(item);
        }, HOVER_INTENT_MS);
      }

      headerWrapper.querySelectorAll('.list-menu__has-mega').forEach(function (item) {
        item.addEventListener('pointerenter', onItemPointerEnter);
        item.addEventListener('pointerleave', function () {
          // A dwell that never completed is abandoned, not banked — leaving
          // before the 120ms is exactly the transit this filters out.
          cancelHoverIntent();
          item.classList.remove('is-hover-open');
          // Leaving the item ends its click-dismissed state (AAS-5493) — the next
          // hover-in opens the flyout normally again.
          item.classList.remove('is-dismissed');
          wakeSync();
        });
        // The <li> box also spans the (now hidden) panel area, so a pointer
        // returning straight to the L1 label never LEAVES the item — without
        // this, the label sat underlined (hover) with a flyout that refused to
        // reopen. Re-entering the label is an explicit "open again". AAS-5493
        var dismissLabel = item.querySelector('.header__menu-item');
        if (dismissLabel) {
          dismissLabel.addEventListener('pointerenter', function () {
            if (item.classList.contains('is-dismissed')) {
              item.classList.remove('is-dismissed');
              wakeSync();
            }
          });
        }
        // Keyboard tab navigation drives the same open/close as hover. focusout
        // with relatedTarget still inside the item (moving between its own links)
        // must NOT close — syncBgLayer reads :focus-within, so only re-sync when
        // focus actually leaves the item.
        item.addEventListener('focusin', onItemOpen);
        item.addEventListener('focusout', function (e) {
          if (!item.contains(e.relatedTarget)) wakeSync();
        });
      });

      // ---- Hold zone: shared flyout controls outside the open item's subtree ----
      // The bottom actions (account / store finder / wishlist) and the traveling
      // notch logo are wrapper-level overlays, NOT descendants of the hovered
      // <li> — entering them drops :hover, so the flyout closed before a click
      // could land (regression from moving the actions out of the per-panel
      // markup). Entering while open re-anchors the open state to the item via
      // .is-held (set synchronously, before the rAF that would start the close;
      // megaSel and the open-state CSS both honour it). Leaving releases it and
      // the flyout closes through its normal path; clicking releases too, so
      // navigation / opened drawers don't leave a held pane behind. Hold is
      // gated on .flyout-open so the fading cluster of a CLOSING flyout can't
      // re-open it when the pointer merely passes through. AAS-1169
      function holdFlyoutOpen() {
        if (!openMegaItem || !headerWrapper.classList.contains('flyout-open')) return;
        openMegaItem.classList.add('is-held');
        wakeSync();
      }
      function releaseFlyoutHold() {
        headerWrapper.querySelectorAll('.list-menu__has-mega.is-held').forEach(function (item) {
          item.classList.remove('is-held');
        });
        wakeSync();
      }
      var holdZones = [];
      var actionsLinks = headerWrapper.querySelector('.header__flyout-actions .atomic-mega__bottom-links');
      if (actionsLinks) holdZones.push(actionsLinks);
      if (logoEl) holdZones.push(logoEl);
      holdZones.forEach(function (zone) {
        zone.addEventListener('pointerenter', holdFlyoutOpen);
        zone.addEventListener('pointerleave', releaseFlyoutHold);
        zone.addEventListener('focusin', holdFlyoutOpen);
        zone.addEventListener('focusout', function (e) {
          if (!zone.contains(e.relatedTarget)) releaseFlyoutHold();
        });
        zone.addEventListener('click', releaseFlyoutHold);
      });

      // ---- Touch two-tap for the desktop nav (AAS-4162) ----
      // The desktop nav opens purely on :hover, but a touch device has no resting
      // hover — a tap on an L1 <a href> just navigates, so the flyout can never be
      // seen. Give touch the standard two-tap: first tap opens the L1's flyout
      // (navigation suppressed), a second tap on the SAME L1 follows the link, and a
      // tap anywhere outside the open item closes it. Gated on the live pointerType
      // (touch/pen) so mouse users on a hybrid device keep instant hover-nav, and on
      // desktopMQ so it never runs while the burger drawer owns the nav.
      var tapOpenItem = null;
      // A touch-primary device (real tablet, or Chrome DevTools device emulation)
      // reports (hover: none) / (pointer: coarse) — the reliable signal, since
      // emulated pointerType is flaky in DevTools. The live pointerType is the
      // fallback that also catches a touch tap on a hybrid laptop whose PRIMARY
      // pointer is a mouse (media query stays false there, so hover-nav is kept for
      // the mouse). Either signal enables the two-tap. (coarseMQ is declared
      // next to desktopMQ — the hover intent reads it too.)
      function isTouchNav() {
        if (!desktopMQ.matches) return false;
        // NOT 'pen' (AAS-5493): an active pen (Wacom, Surface) hovers like a
        // mouse, so hover-nav already works — routing it through two-tap made
        // the first pen click PIN the hover-opened flyout (.is-held), which
        // then hung on screen until a click landed outside it. Hover-less pen
        // setups (e.g. iPad + Apple Pencil) still get two-tap via coarseMQ.
        return coarseMQ.matches || lastPointerType === 'touch';
      }
      function closeTapFlyout() {
        if (!tapOpenItem) return;
        // Drop the hold that pinned the panel open without a hover; syncBgLayer then
        // sees no hover/:focus/.is-held match and runs the normal close. The
        // hover stamp goes with it (AAS-5653): a touch pointer normally fires
        // pointerleave when the finger lifts, but this is the explicit close
        // path and must not depend on that.
        tapOpenItem.classList.remove('is-held', 'is-hover-open');
        setItemExpanded(tapOpenItem, false);
        tapOpenItem = null;
        wakeSync();
      }
      headerWrapper.querySelectorAll('.list-menu__has-mega').forEach(function (item) {
        var link = item.querySelector('.header__menu-item');
        if (!link) return;
        link.addEventListener('click', function (e) {
          if (!isTouchNav()) return; // mouse/keyboard: leave native nav + hover intact
          if (item === tapOpenItem) {
            // Second tap on the already-open L1 — let the link navigate.
            closeTapFlyout();
            return;
          }
          // First tap (or a tap on a different L1 while one is open): open THIS
          // flyout, suppress navigation. onItemOpen wires .is-mega-open / aria /
          // cascade and clears any prior hold; .is-held then pins it open on touch.
          e.preventDefault();
          if (tapOpenItem && tapOpenItem !== item) closeTapFlyout();
          onItemOpen({ currentTarget: item });
          item.classList.add('is-held');
          tapOpenItem = item;
          wakeSync();
        });
      });
      // Is this click on flyout UI that should act normally (NOT a dismiss)?
      // - the open item's own subtree (its L1 label + dropdown panel links)
      // - another L1 with a flyout (a tap there SWITCHES — its own handler runs)
      // - the shared flyout chrome that lives outside the <li>: bottom actions,
      //   traveling logo. (The bare sheet background is not "content" — a tap on
      //   it dismisses.)
      var flyoutActions = headerWrapper.querySelector('.header__flyout-actions');
      function tapIsInsideFlyoutUI(target) {
        if (tapOpenItem && tapOpenItem.contains(target)) return true;
        if (target.closest && target.closest('.list-menu__has-mega')) return true;
        if (flyoutActions && flyoutActions.contains(target)) return true;
        if (logoEl && logoEl.contains(target)) return true;
        return false;
      }
      // Dismiss on an outside tap — in the CAPTURE phase so we can swallow the tap
      // before it reaches the element underneath. A bubble-phase close fires too
      // late: the page content below the open flyout would already have run its own
      // click handler / followed its link. So the first tap outside ONLY closes; a
      // second, deliberate tap then activates whatever is there (AAS-4162).
      document.addEventListener(
        'click',
        function (e) {
          if (!tapOpenItem) return; // nothing tap-opened → let every click through
          if (tapIsInsideFlyoutUI(e.target)) return; // flyout content acts normally
          e.preventDefault();
          e.stopImmediatePropagation();
          closeTapFlyout();
        },
        true
      );

      // ---- Click-dismiss on the open panel's blank background (AAS-5493) ----
      // A click inside the open flyout that hits no interactive content should
      // CLOSE it — before this, the click was a silent no-op and the panel hung
      // under the resting pointer until it wandered out. :hover can't be dropped
      // from JS, so the item is stamped .is-dismissed instead: the megaSel
      // measure and the open-state CSS both skip it, the normal close runs, and
      // pointerleave / the next open clear the stamp.
      headerWrapper.addEventListener('click', function (e) {
        if (!e.target.closest) return;
        // The whole panel surface counts — .mega-menu__content wraps the
        // .container-fluid gutters AND the .atomic-mega column area, and the
        // blank strip the user clicks is as often the gutter as the grid.
        var mega = e.target.closest('.mega-menu__content');
        if (!mega) return;
        if (e.target.closest('a, button, input, select, textarea, summary')) return;
        var item = mega.closest('.list-menu__has-mega');
        if (!item) return;
        item.classList.add('is-dismissed');
        item.classList.remove('is-held');
        // A blank click must not leave focus anywhere inside the item — a
        // lingering :focus-within would hold the dismissed panel on screen
        // (the CSS focus trigger is deliberately exempt from .is-dismissed).
        if (item.contains(document.activeElement)) document.activeElement.blur();
        if (tapOpenItem === item) closeTapFlyout();
        wakeSync();
      });

      // ---- Viewport-capped panel scroll fades (AAS-1169) ----
      // .atomic-mega is capped at 90dvh (component-mega-menu-atomic.css); when
      // the column area overflows, these classes drive the mask fade toward
      // whichever edge hides content. Re-evaluated on scroll and on size
      // changes (web fonts, images, menu edits in the editor) — visibility:
      // hidden panels still have layout, so measuring before first open works.
      headerWrapper.querySelectorAll('.atomic-mega__content-wrapper').forEach(function (scroller) {
        function updateScrollFade() {
          var maxScroll = scroller.scrollHeight - scroller.clientHeight;
          scroller.classList.toggle('can-scroll-more', maxScroll > 1 && scroller.scrollTop < maxScroll - 1);
          scroller.classList.toggle('can-scroll-back', maxScroll > 1 && scroller.scrollTop > 1);
        }
        scroller.addEventListener('scroll', updateScrollFade, { passive: true });
        if ('ResizeObserver' in window) {
          var fadeRO = new ResizeObserver(updateScrollFade);
          fadeRO.observe(scroller);
          // The scroller's client box alone won't report content growth —
          // watch the columns grid too, it's what actually overflows.
          var colGrid = scroller.querySelector('.atomic-mega__columns');
          if (colGrid) fadeRO.observe(colGrid);
        }
        updateScrollFade();
      });

      if (navDrawer) {
        navDrawer.addEventListener('drawer-height-changed', wakeSync);
        // Settle signal for the drawer's open/close slide: it animates via
        // transform, which the panel ResizeObserver can't see. Re-syncs the
        // final geometry after the ease-out tail creeps sub-pixel (the loop
        // rightly idles once ROUNDED values stop changing, a few px short of
        // the true rest position) and backstops any missed wake. Filtered to
        // the drawer element's own transitions — panel/item transitions
        // bubble through here constantly. AAS-1169
        navDrawer.addEventListener('transitionend', function (e) {
          if (e.target === navDrawer) wakeSync();
        });
      }

      wakeSync();
      window.addEventListener('resize', function () {
        measureLogoBase();
        wakeSync();
      });
    }
  })();
